import { API } from 'aws-amplify'
import Footer from '../components/footer'
import Button from '../components/input-components/button'
import Checkbox from '../components/input-components/checkbox'
import ErrorLabel from '../components/input-components/error-label'
import ImageUpload from '../components/input-components/image-upload'
import Label from '../components/input-components/label'
import TextArea from '../components/input-components/textarea'
import TextField from '../components/input-components/textfield'
import Navbar from '../components/navbar'
import NotificationPreview from '../components/notification-preview'
import React, { useState } from 'react'

const NOTIFICATION_TITLE_LIMIT = 50
const NOTIFICATION_MESSAGE_LIMIT = 150

const NotificationsPage: React.FC = () => {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isLoadingIndicator, setLoadingIndicator] = useState(false)
  const [notificationSent, setNotificationSent] = useState(false)
  const [errorOccurred, setErrorHasOccurred] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [sendAsNotification, setSendAsNotification] = useState(true)
  const [saveAsEvent, setSaveAsEvent] = useState(true)

  const handleImageUploaded = (url: string | null): void => {
    setImageUrl(url)
    if (url) {
      setErrorHasOccurred(false)
      setErrorMessage('')
    }
  }

  const handleImageError = (error: string): void => {
    setErrorHasOccurred(true)
    setErrorMessage(error)
  }

  // Determine limits based on checkbox state
  const getTitleMaxLength = (): number | undefined => {
    if (sendAsNotification && !saveAsEvent) return NOTIFICATION_TITLE_LIMIT
    return undefined // No hard limit when event is enabled
  }

  const getMessageMaxLength = (): number | undefined => {
    if (sendAsNotification && !saveAsEvent) return NOTIFICATION_MESSAGE_LIMIT
    return undefined // No hard limit when event is enabled
  }

  const getTitleSoftLimit = (): number | undefined => {
    if (sendAsNotification) return NOTIFICATION_TITLE_LIMIT
    return undefined
  }

  const getMessageSoftLimit = (): number | undefined => {
    if (sendAsNotification) return NOTIFICATION_MESSAGE_LIMIT
    return undefined
  }

  const showCharacterCounters = sendAsNotification

  const isOverNotificationLimit = sendAsNotification && saveAsEvent && (
    title.length > NOTIFICATION_TITLE_LIMIT || message.length > NOTIFICATION_MESSAGE_LIMIT
  )

  const canSubmit = (sendAsNotification || saveAsEvent) && title.trim() !== ''

  const onClick = async (): Promise<void> => {
    if (!canSubmit) return

    try {
      setLoadingIndicator(true)

      if (!notificationSent) {
        console.log('Send Notification ...')

        const payload: {
          title: string
          message: string
          image_url?: string
          send_notification: boolean
          save_as_event: boolean
        } = {
          title,
          message,
          send_notification: sendAsNotification,
          save_as_event: saveAsEvent
        }

        if (imageUrl) {
          payload.image_url = imageUrl
        }

        API.post('MasjidNotifierAPI', '/notification', {
          body: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        setNotificationSent(true)
      }
      setLoadingIndicator(false)
    } catch (error) {
      setErrorHasOccurred(true)
      setErrorMessage('Unable to send notification.')
    }
  }

  const getSuccessMessage = (): string => {
    if (sendAsNotification && saveAsEvent) {
      return 'Successfully sent notification and saved event!'
    } else if (sendAsNotification) {
      return 'Successfully sent notification to all devices!'
    } else {
      return 'Successfully saved event!'
    }
  }

  const getButtonLabel = (): string => {
    if (sendAsNotification && saveAsEvent) {
      return 'Send notification & save as news feed'
    } else if (sendAsNotification) {
      return 'Push notification to all devices'
    } else {
      return 'Save as news feed'
    }
  }

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 flex py-8 px-4">
        <div className="w-full max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <div className="text-gray-600 font-bold">Push Notifications & News</div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Form Section */}
            <div className="flex-1 space-y-4 max-w-md">
              <TextField
                id="title"
                type="text"
                label="Title"
                placeholder="Notification Title - e.g. Eid Namaz on Saturday 9am"
                value={title}
                maxLength={getTitleMaxLength()}
                softLimit={getTitleSoftLimit()}
                showCounter={showCharacterCounters}
                setValue={setTitle}
              />

              <TextArea
                id="message"
                type="text"
                label="Message"
                placeholder="Notification Message e.g. Please come to our new masjid with your prayer mats"
                value={message}
                maxLength={getMessageMaxLength()}
                softLimit={getMessageSoftLimit()}
                showCounter={showCharacterCounters}
                warningMessage={isOverNotificationLimit ? 'Exceeds notification limit. Full text will appear in the news feed in the app.' : undefined}
                setValue={setMessage}
              />

              <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200">
                <Checkbox
                  id="sendAsNotification"
                  label="Send as push notification"
                  checked={sendAsNotification}
                  onChange={setSendAsNotification}
                />
                <Checkbox
                  id="saveAsEvent"
                  label="Save as news feed (viewable in app)"
                  checked={saveAsEvent}
                  onChange={setSaveAsEvent}
                />
                {!sendAsNotification && !saveAsEvent && (
                  <p className="text-xs text-red-500">Please select at least one option</p>
                )}
              </div>

              <ImageUpload id="imageUpload" onImageUploaded={handleImageUploaded} onError={handleImageError} />

              <Button
                id="sendNotification"
                label={getButtonLabel()}
                isLoading={isLoadingIndicator}
                onClick={onClick}
                disabled={!canSubmit}
              />

              <Label id="notificationSent" label={getSuccessMessage()} isVisible={notificationSent} colorType='success' />
              <Label id="notificationSentInfo" label="If you need to send more notifications then you will need to refresh the page." isVisible={notificationSent} />
              <ErrorLabel id="errorLabel" label={errorMessage} isError={errorOccurred} />
            </div>

            {/* Preview Section */}
            {sendAsNotification && (
              <div className="flex-shrink-0 flex justify-center lg:justify-start">
                <NotificationPreview
                  title={title}
                  message={message}
                  imageUrl={imageUrl}
                  titleLimit={NOTIFICATION_TITLE_LIMIT}
                  messageLimit={NOTIFICATION_MESSAGE_LIMIT}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default NotificationsPage
